<template>
    <v-container fluid grid-list-xl class="c-container">
        <h5 class="headline c-mb-3">Nachricht senden</h5>

        <inspire class="c-inspire c-mb-3" :items="inspirations"></inspire>

        <div class="elevation-11 c-recorder">
            <template v-if="state === 'saved'">
                <div class="c-recorder--state">
                    <i style="width: 8rem; height: 4rem;" class="caru-icons sent"></i>
                </div>
            </template>
            <template v-else>
                <div v-if="time" class="c-recorder--state timer">
                    <h3 class="display-2" v-bind:class="{'red--text': lengthWarning }">{{time}}</h3>
                    <div class="caption c-mt-1">Maximal 5 Minuten</div>
                </div>
                <div v-else class="c-recorder--state">
                    <div class="subheading">Hier kannst du deine Nachricht aufnehmen.</div>
                </div>
            </template>
            <div class="c-recorder--record">
                <template v-if="state === 'saved'">
                    <h5 class="headline mb-0">Deine Nachricht wurde erfolgreich verschickt!</h5>
                </template>
                <template v-else>
                    <vu-meter v-if="audioSrc.length === 0" :avgMicFrequency="avgMicFrequency"
                              :enablePulse="state === 'recording'">
                        <v-btn @click="recordHandle" class="c-recorder--record--btn" flat icon>
                            <i v-if="state==='recording'" class="c-recorder--record--icon caru-icons stop"></i>
                            <i v-else class="c-recorder--record--icon caru-icons record"></i>
                        </v-btn>
                    </vu-meter>
                    <audio v-else controls="controls">
                        Your browser does not support the <code>audio</code> element.
                        <source v-bind:src="audioSrc">
                    </audio>
                </template>
            </div>
            <div class="c-recorder--action">
                <template v-if="state === 'saved'">
                    <v-btn class="c-recorder--action--btn mb-0" large color="primary" @click="cancel">
                        Okay
                    </v-btn>
                </template>
                <template v-else>
                    <v-btn v-if="audioSrc.length === 0" disabled class="c-recorder--action--btn" large color="grey">
                        Senden
                    </v-btn>
                    <v-btn v-else class="c-recorder--action--btn" large color="secondary" @click="saveMessage"
                           :loading="messageSaving">Senden
                    </v-btn>
                    <v-btn :disabled="audioSrc.length === 0" flat color="gray" @click="cancel">Abbrechen</v-btn>
                </template>
            </div>
        </div>

        <v-layout row wrap class="c-mb-4">
            <v-flex md12 lg9>
                <div class="subheading c-mb-2">Empfänger</div>
                <div class="c-recipient">
                    <div class="c-recipient--avatar">
                        <v-img class="c-recipient--avatar--img" :src="require('../../assets/avatar.jpg')"></v-img>
                    </div>
                    <div class="c-recipient--content c-card elevation-11">
                        <h5 class="headline c-mb-1">Frida Hess</h5>
                        <div class="subheading">Altersheim Stadtgarten</div>
                        <div class="hr"></div>
                        <div class="c-recipient--info">
                            <router-link class="c-recipient--info--box elevation-6" :to="{name: 'analytics-room'}">
                                <h5 class="headline">
                                    <i class="caru-icons caru-happy-b"></i>
                                </h5>
                                <footer>Status</footer>
                            </router-link>
                            <router-link class="c-recipient--info--box elevation-6"
                                         :to="{name: 'analytics-remotecare'}">
                                <h5 class="headline">{{currentDevice.alarms12h.length}}</h5>
                                <footer>Notrufe</footer>
                            </router-link>
                        </div>
                    </div>
                </div>
            </v-flex>
            <v-flex hidden-md-and-down lg3 class="c-recipient--last">
                <div class="subheading c-mb-2">Neueste Nachricht</div>
                <div class="c-recipient--last--text c-card elevation-11">
                    <h6 v-if="currentDevice.voiceMessages.new.length > 0" class="title">Frida hat sich deine neuen Nachrichten noch nicht angehört</h6>
                    <h6 v-else class="title">Frida hat sich alle Nachrichten von dir angehört.</h6>
                    <v-btn :to="{name: 'voicemessage-all'}" class="ma-0" flat color="primary">
                        Alle Nachrichten
                    </v-btn>
                </div>
            </v-flex>
        </v-layout>

    </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import API, { graphqlOperation } from '@aws-amplify/api'

import { uuid4 } from '../../common/uuid'

import Recorder from '../recoder/recorder'
import getWavLength from '../recoder/wav'

import Inspire from '../components/Inspire'
import VuMeter from '../components/VuMeter'

import prepareQuery from '../prepare.graphql'

const {mapState, mapActions} = createNamespacedHelpers('device')

function strPadLeft (string, pad, length) {
  return (new Array(length + 1).join(pad) + string).slice(-length)
}

const State = {
  READY: 'ready',
  RECORDING: 'recording',
  SAVED: 'saved'
}

const sec = 1000
const min = 60 * sec

const MAX_RECORDING = 5 * min // 05:00 minutes
const MAX_RECORDING_WARNING = 4 * min + 30 * sec // 04:30 minutes

export default {
  name: 'SendView',
  components: {VuMeter, Inspire},
  data () {
    return {
      inspirations: [
        'Erinnerst du dich an einen Ausflug den ihr gemeinsam gemacht habt?',
        'Welches Geschenk von Frida hat dir am meisten Freude bereitet?',
        'Was ist euer gemeinsames Lieblingsspiel?',
        'Künde Frida deinen nächsten Besuch an. Sie freut sich sicher darüber.',
        'Gibt es etwas, dass du mit Frida teilen möchtest?',
        'Möchtest du Frida etwas vorlesen?',
        'Erzähle Frida von einem spannenden Buch, das du gerade liest.'
      ],
      audioSrc: '',
      avgMicFrequency: null,
      state: State.READY,
      animateVUMeter: null,
      recorder: new Recorder(),
      recording: null,
      messageSaving: false,
      timer: null,
      time: null,
      maxLengthTimeout: null,
      maxLengthWarningTimeout: null,
      lengthWarning: false
    }
  },
  computed: {
    ...mapState({
      currentDevice: state => state.currentDevice,
    })
  },
  created () {
  },
  methods: {
    async startRecording () {
      this.audioSrc = ''
      await this.recorder.start()
      this.state = State.RECORDING
      this.updateMicFrequency()
      this.startTimer()
      this.maxLengthTimeout = setTimeout(this.recordHandle, MAX_RECORDING)
      this.maxLengthWarningTimeout = setTimeout(this.showLengthWarning, MAX_RECORDING_WARNING)
    },
    showLengthWarning () {
      this.lengthWarning = true
    },
    startTimer () {
      this.time = '00:00'
      if (this.timer) {
        clearInterval(this.timer)
      }
      let t = 0
      this.timer = setInterval(() => {
        t += 1
        let minutes = Math.floor(t / 60)
        let seconds = t - minutes * 60
        this.time = strPadLeft(minutes, '0', 2) + ':' + strPadLeft(seconds, '0', 2)
      }, 1000)
    },
    async recordHandle () {
      if (this.state === State.READY) {
        this.startRecording()
      } else if (this.state === State.RECORDING) {
        clearInterval(this.timer)
        clearTimeout(this.maxLengthTimeout)
        clearTimeout(this.maxLengthWarningTimeout)
        this.lengthWarning = false
        this.recorder.stop()
        let blob = await this.recorder.getWav()
        let duration = await getWavLength(blob)

        this.recording = {
          blob,
          duration
        }

        this.recorder.releaseMic()
        this.audioSrc = URL.createObjectURL(blob)
      }
    },
    cancel () {
      this.state = State.READY
      this.audioSrc = ''
      this.lengthWarning = false
      clearInterval(this.timer)
      clearTimeout(this.maxLengthTimeout)
      clearTimeout(this.maxLengthWarningTimeout)
      this.time = null
    },
    async saveMessage () {
      this.messageSaving = true
      // Create file name
      const recodingId = uuid4()
      const recodingName = `${recodingId}.wav`

      // Get signed post URL
      const sv = await API.graphql(graphqlOperation(prepareQuery))
      const requestConfig = JSON.parse(sv.data.prepareVoiceMessage)

      let formData = new FormData()
      Object.keys(requestConfig['fields']).forEach(key => {
        formData.append(key, requestConfig.fields[key])
      })

      formData.append('file', this.recording.blob, recodingName)

      await fetch(requestConfig.url, {
        method: 'POST',
        body: formData
      })

      let start = new Date()
      let end = new Date(start.getTime() + 1000 * 60 * 60 * 24)

      await this.saveVoiceMessage({
        deviceIds: [this.currentDevice.device],
        recordingId: recodingId,
        messageDuration: Math.round(this.recording.duration),
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      })

      this.messageSaving = false
      this.state = State.SAVED
    },
    updateMicFrequency () {
      // Stop updating if no longer recording
      if (this.state !== State.RECORDING) {
        window.cancelAnimationFrame(this.animateVUMeter)
        return
      }
      // Grab average microphone frequency
      this.avgMicFrequency = this.recorder.getAverageMicFrequency()

      // Throttle updating slightly
      setTimeout(() => {
        this.animateVUMeter = window.requestAnimationFrame(() => {
          this.updateMicFrequency()
        })
      }, 10)
    },
    ...mapActions(['saveVoiceMessage'])
  }
}
</script>

<style lang="scss">
    @import "../../styles/vars";

    .c-inspire {
        max-width: 30rem;
        height: 10rem;
    }

    .c-recipient {
        position: relative;

        &--content {
            background-color: #fff;
            margin-left: 10%;
            padding: 2rem 3rem 1rem calc(10% + 2rem);
        }

        &--avatar {
            position: absolute;
            left: 0;
            width: 20%;
        }

        &--avatar--img {
            border-radius: 50%;
            width: 100%;
            margin: 2rem auto 0;
        }

        &--info {
            display: flex;
            justify-content: flex-start;
            flex-wrap: wrap;

            a {
                text-decoration: none;
            }

            &--box {
                background-color: $bg-color;
                border-radius: $card-radius;
                width: 30%;
                min-width: 10rem;
                display: flex;
                flex-direction: column;
                margin-bottom: 1rem;
                margin-right: 3rem;

                h5 {
                    padding: 1.5rem 2rem;
                    flex: 1 0 auto;
                    text-align: center;
                    color: rgba(0, 0, 0, 0.87);
                    line-height: 28px !important;

                    i {
                        width: 28px;
                        height: 28px;
                    }
                }

                footer {
                    border-top: 1px solid $grey-lighter;
                    flex: 0 1 auto;
                    color: $primary-color;
                    text-align: center;
                    padding: 1rem;
                    border-bottom-right-radius: $card-radius;
                    border-bottom-left-radius: $card-radius;
                }
            }
        }
    }

    .c-recipient--last {
        align-items: stretch;
        display: flex;
        flex-direction: column;

        &--text {
            flex: 1 0 auto;
            display: flex;
            flex-direction: column;
            padding: 2rem;

            h6 {
                flex: 1 0 auto;
            }
        }
    }

    .c-recorder {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        background-color: white;
        margin-top: 5rem;
        margin-bottom: 5rem;
        border-radius: $card-radius;

        &--state, &--record, &--action {
            display: flex;
            align-items: center;
            align-content: center;
            padding: 3rem;
            justify-content: center;

        }

        &--state {
            min-height: 226px;
            width: 30%;
            min-width: 13rem;
            box-shadow: 11px 0 7px -4px rgba(0, 0, 0, 0.08);
            z-index: 2;
            flex-direction: column;

            &.timer {
                h3 {
                    font-family: monospace !important;
                }
            }
        }

        &--record {
            width: 50%;
            box-shadow: 11px 0 7px -4px rgba(0, 0, 0, 0.08);
            z-index: 1;

            &--btn {
                background-color: white;
            }

            &--icon, &--btn {
                width: 5rem;
                height: 5rem;

            }

            &_r {
                background-color: $primary-color;
            }
        }

        &--action {
            width: 20%;
            min-width: 15rem;
            flex-direction: column;

            &--btn {
                color: white !important;
                width: 70%;
                margin-bottom: 2rem;
            }
        }
    }
</style>
